/** Header-action builder for the project Health &amp; Safety tab.
 *  ProjectHSTab owns a hub grid (RAMS/Toolbox Talks/Plant Checks/Inductions/
 *  Near Miss) and drills into whichever live section's list/record/editor.
 *  The list view draws the section's create pill; the record view draws that
 *  section's lifecycle and editor actions. No JSX. */

import {
  Plus,
  LayoutTemplate,
  FilePlus2,
  Edit2,
  SendHorizonal,
  Rocket,
  MoreHorizontal,
  FileDown,
  BookmarkPlus,
  Archive,
  RotateCcw,
  Undo2,
  CheckCheck,
  Trash2,
  Loader2,
  Save,
  Info,
  ArrowLeft,
  ArrowRight,
} from '@/shared/ui/icons';
import { HS_DOCUMENT_SECTION_ITEMS } from '../../../app/navigation';
import type { MobileHeaderAction, MobileHeaderSubAction } from './types';
import type { RamsStatus, ToolboxTalkStatus, NearMissStatus } from '../../../types/healthSafety';
import type { PlantCheckStatus } from '../../../types/plant';

export interface HSHeaderState {
  view: 'hub' | 'list' | 'record' | 'editor';
  count: number;
  /** Which H&amp;S section this state describes. Absent is treated as 'rams'
   *  so native shells and any other pre-Toolbox-Talks consumer keep working
   *  unchanged. 'register' is the workspace-wide plant register, not the
   *  weekly plant-check sheets ('plant') — see buildHSHeaderActions below. */
  section?: 'rams' | 'toolbox' | 'plant' | 'register' | 'near-miss';
  /** Only meaningful on the record view. */
  status?: RamsStatus | ToolboxTalkStatus | PlantCheckStatus | NearMissStatus;
  canIssue?: boolean;
}

export const HS_SECTION_LABELS = {
  rams: 'RAMS', toolbox: 'Toolbox talks', plant: 'Plant checks',
  register: 'Plant register', 'near-miss': 'Near miss',
} as const;

export interface HSHeaderCtx {
  hsHeaderState: HSHeaderState;
  hsEditorState?: HSEditorHeaderState | null;
  hsDocumentState?: HSDocumentHeaderState | null;
  leadingActions: MobileHeaderAction[];
  trailingActions: MobileHeaderAction[];
}

export interface HSEditorHeaderState {
  isSaving: boolean;
  canAddItems?: boolean;
  saveLabel: string;
  activeSection?: string;
  sectionIds?: readonly string[];
}

export interface HSDocumentHeaderState {
  activeSection: string;
  sectionIds: readonly string[];
}

export function getHSSectionLabel(sectionId: string | undefined): string | undefined {
  return HS_DOCUMENT_SECTION_ITEMS.find((item) => item.id === sectionId)?.label;
}

export function getHSEditorAddLabel(sectionId: string | undefined): string | undefined {
  const labels: Partial<Record<string, string>> = {
    'rams-standards': 'Add standard',
    'rams-drawings': 'Add document',
    'rams-responsibilities': 'Add role',
    'rams-plant': 'Add plant',
    'rams-permits': 'Add check',
    'rams-hold-points': 'Add hold point',
    'rams-environmental': 'Add control',
    'rams-quality-records': 'Add record',
    'rams-handover': 'Add item',
    'rams-references': 'Add reference',
    'rams-distribution': 'Add recipient',
    'rams-coshh': 'Add substance',
    'rams-method': 'Add step',
    'rams-risks': 'Add risk',
  };
  return labels[sectionId ?? ''];
}

function appendSectionNavigation(
  state: HSDocumentHeaderState | null | undefined,
  editor: boolean,
  trailingActions: MobileHeaderAction[],
  disabled = false,
): void {
  if (!state?.sectionIds.length || !state.activeSection) return;
  const index = state.sectionIds.indexOf(state.activeSection);
  if (index < 0) return;
  const navigateEvent = editor ? 'hs:navigate-editor-section' : 'hs:navigate-document-section';
  const prefix = editor ? 'hs-editor' : 'hs-document';
  const previous = state.sectionIds[index - 1];
  const next = state.sectionIds[index + 1];
  for (const [direction, sectionId, icon] of [
    ['previous', previous, ArrowLeft],
    ['next', next, ArrowRight],
  ] as const) {
    const label = direction === 'previous' ? 'Previous section' : 'Next section';
    const targetLabel = getHSSectionLabel(sectionId);
    trailingActions.push({
      id: `${prefix}-${direction}`,
      icon,
      ariaLabel: targetLabel ? `${label}: ${targetLabel}` : label,
      group: 'page',
      disabled: disabled || !sectionId,
      onClick: () => {
        if (sectionId && !disabled) window.dispatchEvent(new CustomEvent(navigateEvent, { detail: { sectionId } }));
      },
    });
  }
}

/** Appends the H&amp;S trailing pills for whichever RAMS view is active.
 *  Called only when `hsHeaderState` is non-null. */
export function buildHSHeaderActions(ctx: HSHeaderCtx): void {
  const { hsHeaderState, hsEditorState, hsDocumentState, trailingActions } = ctx;

  // The register's pills are built by buildPlantHeaderActions at the
  // UnifiedAppHeader level (driven by the header's own global PLANT_STATE_EVENT
  // listener), so they can't drift from the standalone /plant screen's.
  // UnifiedAppHeader branches to that builder before ever reaching this one;
  // this is a defensive no-op for section 'register', not the live path.
  if (hsHeaderState.section === 'register') return;

  if (hsHeaderState.view === 'editor') {
    const isSaving = hsEditorState?.isSaving ?? false;
    appendSectionNavigation(
      hsEditorState?.activeSection && hsEditorState.sectionIds
        ? { activeSection: hsEditorState.activeSection, sectionIds: hsEditorState.sectionIds }
        : null,
      true,
      trailingActions,
      isSaving,
    );
    const addLabel = getHSEditorAddLabel(hsEditorState?.activeSection);
    if (addLabel && hsEditorState?.canAddItems !== false) {
      trailingActions.push({
        id: 'hs-editor-add-item',
        icon: Plus,
        label: addLabel,
        ariaLabel: addLabel,
        group: 'create',
        disabled: isSaving,
        onClick: () => window.dispatchEvent(new CustomEvent('hs:editor-add-item', {
          detail: { sectionId: hsEditorState?.activeSection },
        })),
      });
    }
    trailingActions.push(
      {
        id: 'hs-editor-cancel',
        label: 'Cancel',
        ariaLabel: 'Cancel changes',
        disabled: isSaving,
        group: 'page',
        onClick: () => window.dispatchEvent(new CustomEvent('hs:editor-cancel')),
      },
      {
        id: 'hs-editor-save',
        icon: isSaving ? Loader2 : Save,
        label: isSaving ? 'Saving...' : hsEditorState?.saveLabel ?? 'Save changes',
        ariaLabel: isSaving ? 'Saving changes' : 'Save changes',
        disabled: isSaving,
        group: 'page',
        onClick: () => window.dispatchEvent(new CustomEvent('hs:editor-save')),
      },
    );
    return;
  }

  const section = hsHeaderState.section ?? 'rams';
  if (section === 'toolbox') {
    buildToolboxTalksHeaderActions(ctx);
    return;
  }
  if (section === 'plant') {
    buildPlantChecksHeaderActions(ctx);
    return;
  }
  if (section === 'near-miss') {
    buildNearMissHeaderActions(ctx);
    return;
  }

  if (hsHeaderState.view === 'list') {
    trailingActions.push({
      id: 'hs-create',
      icon: Plus,
      ariaLabel: 'New RAMS',
      group: 'create',
      // Load-bearing, not decoration: a pill with neither onClick nor
      // subActions renders dead and stops collapsing into the overflow sheet.
      subActions: [
        {
          id: 'hs-create-template',
          icon: LayoutTemplate,
          label: 'From template',
          onClick: () => window.dispatchEvent(new CustomEvent('hs:from-template')),
        },
        {
          id: 'hs-create-blank',
          icon: FilePlus2,
          label: 'Blank RAMS',
          onClick: () => window.dispatchEvent(new CustomEvent('hs:create')),
        },
      ],
    });
    return;
  }

  if (hsHeaderState.view !== 'record' || !hsHeaderState.status) return;

  const status = hsHeaderState.status as RamsStatus;

  appendSectionNavigation(hsDocumentState, false, trailingActions);

  trailingActions.push({
    id: 'hs-details',
    icon: Info,
    label: 'Details',
    ariaLabel: 'RAMS details',
    group: 'page',
    onClick: () => window.dispatchEvent(new CustomEvent('hs:toggle-details')),
  });

  // Edit — draft only. Not part of the progress/create/output trio the
  // document brief scopes; added because RAMSEditor stays a separate Phase 1
  // view and a draft record needs a way back into it now the bottom action
  // bar is gone.
  if (status === 'draft') {
    trailingActions.push({
      id: 'hs-edit',
      icon: Edit2,
      label: 'Edit',
      ariaLabel: 'Edit RAMS',
      group: 'modify',
      onClick: () => window.dispatchEvent(new CustomEvent('hs:edit')),
    });
  }

  // Progress — the single status-advancing action. Submitting opens the
  // recipient-picker dialog rather than transitioning directly (the actual
  // draft->submitted call happens once reviewers are chosen); approval of a
  // submitted record is the EMPLOYER's action on the public review page, not
  // something the contractor side can do from here, so 'submitted' has no
  // progress pill at all — the editable Word RAMS pack is available from the
  // overflow when the user needs a client hand-off.
  if (status === 'draft') {
    trailingActions.push({
      id: 'hs-progress',
      icon: SendHorizonal,
      label: 'Send for review',
      ariaLabel: 'Send RAMS for review',
      group: 'progress',
      disabled: !hsHeaderState.canIssue,
      onClick: () => window.dispatchEvent(new CustomEvent('hs:submit-review')),
    });
  } else if (status === 'approved') {
    trailingActions.push({
      id: 'hs-progress',
      icon: Rocket,
      label: 'Issue',
      ariaLabel: 'Issue RAMS',
      group: 'progress',
      onClick: () => window.dispatchEvent(new CustomEvent('hs:transition', { detail: { to: 'issued' } })),
    });
  }

  // Create — New version. Available on approved and issued (not just
  // issued): versioning an approved-but-not-yet-issued document to fix
  // something before it goes live is legitimate, same as versioning an
  // issued one — and on archived, to revive an old document as a fresh draft.
  if (status === 'issued' || status === 'approved' || status === 'archived') {
    trailingActions.push({
      id: 'hs-new-version',
      icon: FilePlus2,
      label: 'New version',
      ariaLabel: 'Create new version',
      group: 'create',
      onClick: () => window.dispatchEvent(new CustomEvent('hs:new-version')),
    });
  }

  // Output — overflow. The export is a single client-ready ZIP containing the
  // editable Word RAMS and every assigned COSHH PDF.
  const outputActions: MobileHeaderSubAction[] = [
    {
      id: 'hs-download-pdf',
      icon: FileDown,
      label: 'Download RAMS pack',
      onClick: () => window.dispatchEvent(new CustomEvent('hs:download-pdf')),
    },
  ];
  // Recall to draft — submitted only. A submitted record has no in-app edit
  // path (hs_rams_routes.tsx's PUT rejects anything but a draft, so the
  // Employer is always reviewing exactly what was sent) — this is how a
  // contractor fixes a mistake spotted after sending: pull it back, edit,
  // resubmit. submitted->draft is already a legal transition.
  if (status === 'submitted') {
    outputActions.push({
      id: 'hs-recall-draft',
      icon: Undo2,
      label: 'Recall to draft',
      onClick: () => window.dispatchEvent(new CustomEvent('hs:transition', { detail: { to: 'draft' } })),
    });
  }
  if (status === 'approved' || status === 'issued') {
    outputActions.push({
      id: 'hs-save-template',
      icon: BookmarkPlus,
      label: 'Save as template',
      onClick: () => window.dispatchEvent(new CustomEvent('hs:save-template')),
    });
  }
  // Archive — issued only. The server's transition map (RAMS_ALLOWED_TRANSITIONS
  // in ramsUtils.ts) doesn't allow approved->archived: an approved-but-not-yet-
  // issued record goes forward to issued or back to draft, never straight to
  // archived.
  if (status === 'issued') {
    outputActions.push({
      id: 'hs-archive',
      icon: Archive,
      label: 'Archive',
      onClick: () => window.dispatchEvent(new CustomEvent('hs:transition', { detail: { to: 'archived' } })),
    });
  }
  if (status === 'archived') {
    outputActions.push({
      id: 'hs-restore-draft',
      icon: RotateCcw,
      label: 'Restore to draft',
      onClick: () => window.dispatchEvent(new CustomEvent('hs:transition', { detail: { to: 'draft' } })),
    });
  }
  // Delete — draft or archived only. Submitted/approved/issued records are
  // controlled documents: the path off them is Archive first, then Delete.
  if (status === 'draft' || status === 'archived') {
    outputActions.push({
      id: 'hs-delete',
      icon: Trash2,
      label: 'Delete',
      onClick: () => window.dispatchEvent(new CustomEvent('hs:delete')),
    });
  }
  trailingActions.push({
    id: 'hs-output',
    icon: MoreHorizontal,
    ariaLabel: 'More actions',
    group: 'output',
    subActions: outputActions,
  });
}

/** Toolbox Talks pills — far simpler than RAMS's: two statuses, no review
 *  workflow, no templates, no versioning. Called only when
 *  hsHeaderState.section === 'toolbox'; the editor view is handled by the
 *  generic Cancel/Save branch above `buildHSHeaderActions` delegates from. */
function buildToolboxTalksHeaderActions(ctx: HSHeaderCtx): void {
  const { hsHeaderState, trailingActions } = ctx;

  if (hsHeaderState.view === 'list') {
    trailingActions.push({
      id: 'hs-toolbox-create',
      icon: Plus,
      ariaLabel: 'New toolbox talk',
      group: 'create',
      onClick: () => window.dispatchEvent(new CustomEvent('hs:toolbox-create')),
    });
    return;
  }

  if (hsHeaderState.view !== 'record' || !hsHeaderState.status) return;

  const status = hsHeaderState.status as ToolboxTalkStatus;

  // Edit — always available. A toolbox talk has no controlled-document edit
  // lock the way a submitted/issued RAMS does (hs_toolbox_talk_routes.tsx's
  // PUT accepts any status), so unlike RAMS's Edit this isn't draft-only.
  trailingActions.push({
    id: 'hs-toolbox-edit',
    icon: Edit2,
    label: 'Edit',
    ariaLabel: 'Edit toolbox talk',
    group: 'modify',
    onClick: () => window.dispatchEvent(new CustomEvent('hs:toolbox-edit')),
  });

  // Progress — the single status-advancing action, draft only. There is no
  // review workflow to gate this on, unlike RAMS's canIssue check.
  if (status === 'draft') {
    trailingActions.push({
      id: 'hs-toolbox-deliver',
      icon: CheckCheck,
      label: 'Mark delivered',
      ariaLabel: 'Mark toolbox talk delivered',
      group: 'progress',
      onClick: () => window.dispatchEvent(new CustomEvent('hs:toolbox-deliver')),
    });
  }

  const outputActions: MobileHeaderSubAction[] = [
    {
      id: 'hs-toolbox-download',
      icon: FileDown,
      label: 'Download sign-in sheet',
      onClick: () => window.dispatchEvent(new CustomEvent('hs:toolbox-download')),
    },
  ];
  // Back to draft — delivered only, the mirror of the draft-only progress
  // pill above. A toolbox talk is not a controlled document, so there is no
  // recall/restore distinction like RAMS has between 'submitted' and
  // 'archived' — one reverse transition covers it.
  if (status === 'delivered') {
    outputActions.push({
      id: 'hs-toolbox-back-to-draft',
      icon: Undo2,
      label: 'Back to draft',
      onClick: () => window.dispatchEvent(new CustomEvent('hs:toolbox-undeliver')),
    });
  }
  outputActions.push({
    id: 'hs-toolbox-delete',
    icon: Trash2,
    label: 'Delete',
    onClick: () => window.dispatchEvent(new CustomEvent('hs:toolbox-delete')),
  });
  trailingActions.push({
    id: 'hs-output',
    icon: MoreHorizontal,
    ariaLabel: 'More actions',
    group: 'output',
    subActions: outputActions,
  });
}

/** Near Miss pills — same shape as Toolbox Talks' (two statuses, no review
 *  workflow, no versioning), renamed for the report lifecycle: close/reopen
 *  rather than deliver/undeliver. Called only when
 *  hsHeaderState.section === 'near-miss'; the editor view is handled by the
 *  generic Cancel/Save branch above `buildHSHeaderActions` delegates from. */
function buildNearMissHeaderActions(ctx: HSHeaderCtx): void {
  const { hsHeaderState, trailingActions } = ctx;

  if (hsHeaderState.view === 'list') {
    trailingActions.push({
      id: 'hs-near-miss-create',
      icon: Plus,
      ariaLabel: 'Log near miss',
      group: 'create',
      onClick: () => window.dispatchEvent(new CustomEvent('hs:near-miss-create')),
    });
    return;
  }

  if (hsHeaderState.view !== 'record' || !hsHeaderState.status) return;

  const status = hsHeaderState.status as NearMissStatus;

  // Edit — always available. A near miss report has no controlled-document
  // edit lock the way a submitted/issued RAMS does (hs_near_miss_routes.tsx's
  // PUT accepts any status), so unlike RAMS's Edit this isn't open-only.
  trailingActions.push({
    id: 'hs-near-miss-edit',
    icon: Edit2,
    label: 'Edit',
    ariaLabel: 'Edit near miss report',
    group: 'modify',
    onClick: () => window.dispatchEvent(new CustomEvent('hs:near-miss-edit')),
  });

  // Progress — the single status-advancing action, open only. There is no
  // review workflow to gate this on, unlike RAMS's canIssue check.
  if (status === 'open') {
    trailingActions.push({
      id: 'hs-near-miss-close',
      icon: CheckCheck,
      label: 'Mark closed',
      ariaLabel: 'Mark near miss report closed',
      group: 'progress',
      onClick: () => window.dispatchEvent(new CustomEvent('hs:near-miss-close')),
    });
  }

  const outputActions: MobileHeaderSubAction[] = [];
  // Reopen — closed only, the mirror of the open-only progress pill above. A
  // near miss report is not a controlled document, so there is no recall/
  // restore distinction like RAMS has between 'submitted' and 'archived' —
  // one reverse transition covers it.
  if (status === 'closed') {
    outputActions.push({
      id: 'hs-near-miss-reopen',
      icon: Undo2,
      label: 'Reopen',
      onClick: () => window.dispatchEvent(new CustomEvent('hs:near-miss-reopen')),
    });
  }
  outputActions.push({
    id: 'hs-near-miss-delete',
    icon: Trash2,
    label: 'Delete',
    onClick: () => window.dispatchEvent(new CustomEvent('hs:near-miss-delete')),
  });
  trailingActions.push({
    id: 'hs-output',
    icon: MoreHorizontal,
    ariaLabel: 'More actions',
    group: 'output',
    subActions: outputActions,
  });
}

/** Plant Checks pills — same shape as Toolbox Talks' (two statuses, no review
 *  workflow), renamed for the machine-check lifecycle: complete/reopen rather
 *  than deliver/undeliver. Called only when hsHeaderState.section === 'plant';
 *  the editor view (filling in the 49 rated rows) is handled by the generic
 *  Cancel/Save branch above `buildHSHeaderActions` delegates from. There is no
 *  blank-create sub-action here: a sheet has no groups to rate without a
 *  template, so the single create pill always opens the template picker. */
function buildPlantChecksHeaderActions(ctx: HSHeaderCtx): void {
  const { hsHeaderState, trailingActions } = ctx;

  if (hsHeaderState.view === 'list') {
    trailingActions.push({
      id: 'hs-plant-create',
      icon: Plus,
      ariaLabel: 'New plant check',
      group: 'create',
      onClick: () => window.dispatchEvent(new CustomEvent('plant:check-create')),
    });
    return;
  }

  if (hsHeaderState.view !== 'record' || !hsHeaderState.status) return;

  const status = hsHeaderState.status as PlantCheckStatus;

  // Edit — always available, same as Toolbox Talks: a plant check has no
  // controlled-document edit lock, so filling in ratings after completion
  // (correcting a mistake) is a legal edit, not a recall-to-draft dance.
  trailingActions.push({
    id: 'hs-plant-edit',
    icon: Edit2,
    label: 'Edit',
    ariaLabel: 'Edit plant check',
    group: 'modify',
    onClick: () => window.dispatchEvent(new CustomEvent('plant:check-edit')),
  });

  // Progress — the single status-advancing action, draft only.
  if (status === 'draft') {
    trailingActions.push({
      id: 'hs-plant-complete',
      icon: CheckCheck,
      label: 'Mark complete',
      ariaLabel: 'Mark plant check complete',
      group: 'progress',
      onClick: () => window.dispatchEvent(new CustomEvent('plant:check-complete')),
    });
  }

  const outputActions: MobileHeaderSubAction[] = [
    {
      id: 'hs-plant-download',
      icon: FileDown,
      label: 'Download PDF',
      onClick: () => window.dispatchEvent(new CustomEvent('plant:check-download')),
    },
  ];
  // Back to draft — completed only, the mirror of the draft-only progress
  // pill above.
  if (status === 'completed') {
    outputActions.push({
      id: 'hs-plant-reopen',
      icon: Undo2,
      label: 'Reopen',
      onClick: () => window.dispatchEvent(new CustomEvent('plant:check-reopen')),
    });
  }
  // Save as template — completed only, mirroring RAMS's approved/issued gate:
  // a check's checklist is only worth capturing as a reusable starting point
  // once it's a finished record, not one still being filled in.
  if (status === 'completed') {
    outputActions.push({
      id: 'hs-plant-save-template',
      icon: BookmarkPlus,
      label: 'Save as template',
      onClick: () => window.dispatchEvent(new CustomEvent('plant:check-save-template')),
    });
  }
  outputActions.push({
    id: 'hs-plant-delete',
    icon: Trash2,
    label: 'Delete',
    onClick: () => window.dispatchEvent(new CustomEvent('plant:check-delete')),
  });
  trailingActions.push({
    id: 'hs-output',
    icon: MoreHorizontal,
    ariaLabel: 'More actions',
    group: 'output',
    subActions: outputActions,
  });
}
